import type { FC } from 'react';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Input,
  Text,
  VStack,
  theme,
} from '@squire/ui';

import { BottomSheetInput } from '@/components/_shared/BottomSheet';
import { PhoneFormField } from '@/components/_shared/PhoneFormField';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { UserInformationFormComponentProps } from './types';

const UserInformationForm: FC<UserInformationFormComponentProps> = ({
  firstFieldRef,
  title,
  labels,
  defaultCountry,
  insideBottomSheet = true,
  phoneFirst = false,
  stackNameFields = false,
  placeholders,
  phoneDisabled = false,
}) => {
  const { t } = useScreenTranslations('ConfirmBookingBottomSheet');
  const resolvedTitle =
    title === undefined ? t('userInformation.title') : title;
  const InputComponent = insideBottomSheet ? BottomSheetInput : Input;

  const firstNameField = (
    <FormField
      name="firstName"
      render={({
        field: { value, onBlur, onChange },
        fieldState: { error },
      }) => (
        <FormItem>
          {labels?.firstName ? <FormLabel>{labels.firstName}</FormLabel> : null}
          <FormControl>
            <InputComponent
              ref={firstFieldRef}
              testID="first-name-input"
              placeholder={
                placeholders?.firstName ??
                t('userInformation.firstNamePlaceholder')
              }
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              autoCapitalize="words"
              autoComplete="given-name"
              error={!!error?.message}
              accessibilityLabel={t(
                'userInformation.accessibility.firstNameInput',
              )}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const lastNameField = (
    <FormField
      name="lastName"
      render={({
        field: { value, onBlur, onChange },
        fieldState: { error },
      }) => (
        <FormItem>
          {labels?.lastName ? <FormLabel>{labels.lastName}</FormLabel> : null}
          <FormControl>
            <InputComponent
              testID="last-name-input"
              placeholder={
                placeholders?.lastName ??
                t('userInformation.lastNamePlaceholder')
              }
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              autoCapitalize="words"
              autoComplete="family-name"
              error={!!error?.message}
              accessibilityLabel={t(
                'userInformation.accessibility.lastNameInput',
              )}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const nameFields = stackNameFields ? (
    <VStack space={theme.spacing[4]}>
      {firstNameField}
      {lastNameField}
    </VStack>
  ) : (
    <HStack style={styles.nameFieldsContainer}>
      <VStack flex>{firstNameField}</VStack>
      <VStack flex>{lastNameField}</VStack>
    </HStack>
  );

  const phoneField = (
    <VStack space={theme.spacing[2]}>
      {labels?.phone ? (
        // Matches FormLabel's actual rendered weight (regular, not bold) —
        // the phone label sits outside the phone FormField/FormItem's
        // context, so it can't use FormLabel directly.
        <Text variant="m" color="textSecondary">
          {labels.phone}
        </Text>
      ) : null}
      <PhoneFormField
        name="phone"
        defaultCountry={defaultCountry}
        testID="phone-input"
        placeholder={
          placeholders?.phone ?? t('userInformation.phonePlaceholder')
        }
        disabled={phoneDisabled}
        accessibilityLabel={t('userInformation.accessibility.phoneInput')}
        accessibilityHint={t('userInformation.accessibility.phoneInputHint')}
      />
    </VStack>
  );

  return (
    <VStack space={theme.spacing[4]}>
      {resolvedTitle ? (
        <Text variant="lStrong" color="textPrimary">
          {resolvedTitle}
        </Text>
      ) : null}

      <VStack space={theme.spacing[4]}>
        {phoneFirst ? phoneField : null}
        {nameFields}
        {phoneFirst ? null : phoneField}

        <VStack>
          <FormField
            name="email"
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <FormItem>
                {labels?.email ? <FormLabel>{labels.email}</FormLabel> : null}
                <FormControl>
                  <InputComponent
                    testID="email-input"
                    placeholder={
                      placeholders?.email ??
                      t('userInformation.emailPlaceholder')
                    }
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    error={!!error?.message}
                    accessibilityLabel={t(
                      'userInformation.accessibility.emailInput',
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </VStack>
      </VStack>
    </VStack>
  );
};

UserInformationForm.displayName = 'UserInformationForm';
export { UserInformationForm };
